import React from "react";
import { useMemo, useState } from "react";

import { QueryKey, useQuery } from "react-query";

import { ColumnDef, flexRender, getCoreRowModel, PaginationState, Row, useReactTable } from "@tanstack/react-table";
import { ChevronDoubleLeftIcon, ChevronDoubleRightIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/outline";

import LoadingWheel from "../LoadingWheel";
import { useTableFilter } from "./TableFilterContext";
import useQSBuilder from "../../../hooks/useQSBuilder";
import useApiHelper from "../../../services/useApiHelper";
import { IBaseEntity } from "../../../models/system/BaseEntity";
import { IDefaultPaginatedResponse } from "../../../models/system/DefaultResponse";
import clsx from "clsx";

export interface TableProps<T extends IBaseEntity> {
  url: string;
  queryKey: QueryKey;
  columns: ColumnDef<T, any>[];
  queryString?: Record<string, string | number | boolean | undefined>;
  rowsPerPage?: number;
  searchTerm?: string;
  onRowClick?: (row: Row<T>) => void;
  enablePagination?: boolean;
  emptyMessage?: string;
  tableClasses?: string;
}

export function Table<T extends IBaseEntity>({
  url,
  queryKey,
  columns,
  queryString,
  rowsPerPage = 10,
  searchTerm,
  onRowClick,
  enablePagination = true,
  emptyMessage,
  tableClasses,
}: TableProps<T>) {
  return (
    <TableImpl
      url={url}
      queryKey={queryKey}
      columns={columns}
      queryString={queryString}
      rowsPerPage={rowsPerPage}
      searchTerm={searchTerm}
      onRowClick={onRowClick}
      enablePagination={enablePagination}
      emptyMessage={emptyMessage}
      tableClasses={tableClasses}
    />
  );
}

function TableImpl<T extends IBaseEntity>({
  url,
  queryKey,
  columns,
  queryString,
  rowsPerPage,
  searchTerm,
  onRowClick,
  enablePagination = true,
  emptyMessage,
  tableClasses,
}: TableProps<T>) {
  const { get } = useApiHelper();
  const buildQS = useQSBuilder();
  const { filters, getFilterQueryString } = useTableFilter();
  const defaultData = useMemo(() => [], []);
  const pageSizeOptions = [5, 10, 20, 30, 40, 50];
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: rowsPerPage ?? 10,
  });

  const fetchDataOptions = enablePagination ? {
    pageNumber: pageIndex + 1,
    itemsPerPage: pageSize,
  } : {};

  const qk = [
    ...queryKey,
    ...(enablePagination ? [pageIndex, pageSize] : []),
    ...(filters ? Object.entries(filters).flat() : []),
    ...(queryString ? Object.entries(queryString).flat() : []),
    ...(searchTerm ? Object.entries(searchTerm).flat() : []),
  ]

  const dataQuery = useQuery(
    qk,
    () =>
      get(
        `${url}${buildQS({ ...fetchDataOptions, ...queryString, searchTerm })}${getFilterQueryString()}`
      ).then((res: any) => res) as unknown as IDefaultPaginatedResponse<T>
  );

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  );

  const table = useReactTable({
    data: dataQuery.data?.data ?? defaultData,
    columns,
    pageCount: enablePagination && dataQuery.data?.count
      ? Math.ceil(dataQuery.data.count / pageSize)
      : 1,
    state: enablePagination ? {
      pagination,
    } : {},
    onPaginationChange: enablePagination ? setPagination : undefined,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: enablePagination,
  });



  if (dataQuery.isLoading) {
    return <div className="py-10 flex items-center justify-center">
      <LoadingWheel width="w-10" height="h-10" />
    </div>;
  } else {
    return (
      <div>
        <div className={clsx("-mx-4 mt-8 overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:-mx-6 md:mx-0 md:rounded-lg", tableClasses)}>
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="py-3.5 !pl-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={table.getAllLeafColumns().length} className="py-6 text-center text-sm italic text-gray-500">
                    {emptyMessage ?? 'No items found.'}
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row, rowIndex) => (
                  <tr
                    key={row.id}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    className={onRowClick ? `hover:bg-gray-100 cursor-pointer` : ''}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="whitespace-nowrap px-3 py-4 text-sm"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {enablePagination && (
          <div className="w-full ml-auto mt-2 px-2 flex items-center flex-row justify-between text-sm">
            <span className="flex items-center space-x-2">
              <div>Page</div>
              <strong>
                {table.getState().pagination.pageIndex + 1} of{" "}
                {table.getPageCount()}
              </strong>
            </span>
            <div>
              <button
                type="button"
                className={`rounded-full p-2 text-gray-600 hover:cursor-pointer hover:bg-gray-200 
            ${!table.getCanPreviousPage() &&
                  "hover:!cursor-default !text-gray-400 hover:!bg-paper"
                  }
            `}
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
              >
                <ChevronDoubleLeftIcon className="w-5 h-5" />
              </button>
              <button
                type="button"
                className={`-ml-1 rounded-full p-2 text-gray-600 hover:cursor-pointer hover:bg-gray-200 
            ${!table.getCanPreviousPage() &&
                  "hover:!cursor-default !text-gray-400 hover:!bg-paper"
                  }
            `}
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <ChevronLeftIcon className="w-5 h-5" />
              </button>
              <button
                type="button"
                className={`-ml-1 rounded-full p-2 text-gray-600 hover:cursor-pointer hover:bg-gray-200 
            ${!table.getCanNextPage() &&
                  "hover:!cursor-default !text-gray-400 hover:!bg-paper"
                  }
            `}
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                <ChevronRightIcon className="w-5 h-5" />
              </button>
              <button
                type="button"
                className={`-ml-1 rounded-full p-2 text-gray-600 hover:cursor-pointer hover:bg-gray-200 
            ${!table.getCanNextPage() &&
                  "hover:!cursor-default !text-gray-400 hover:!bg-paper"
                  }
            `}
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
              >
                <ChevronDoubleRightIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <span>Per Page</span>
              <select
                value={table.getState().pagination.pageSize}
                onChange={(e) => {
                  table.setPageSize(Number(e.currentTarget.value));
                }}
              >
                {pageSizeOptions.map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    {pageSize}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
    );
  }
}
